// src/components/analytics/predictors/LinearRegressionPredictor.js

import { BasePredictor } from './base/BasePredictor';
import { 
  parseDate, 
  formatDate, 
  dateToNumeric, 
  numericToDate, 
  analyzeDateFrequency,
  generateFutureDatesWithFrequency,
  detectDateFormat
} from '../utils/dateUtils';
import { format } from 'date-fns';

export class LinearRegressionPredictor extends BasePredictor {
  constructor(config) {
    super(config);
    this.method = 'linear_regression';
  }

  /**
   * Calculate linear regression coefficients
   * @param {Array} points - Array of {x: numeric, y: numeric} points
   * @returns {Object} - { slope, intercept, r2 }
   */
  calculateRegression(points) {
    const n = points.length;
    
    if (n < 2) {
      throw new Error('At least 2 points required for linear regression');
    }

    // Calculate sums
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;
    let sumY2 = 0;

    for (const point of points) {
      sumX += point.x;
      sumY += point.y;
      sumXY += point.x * point.y;
      sumX2 += point.x * point.x;
      sumY2 += point.y * point.y;
    }

    // Calculate slope and intercept
    const denominator = (n * sumX2 - sumX * sumX);
    
    if (Math.abs(denominator) < 1e-10) {
      // Handle case where all x values are the same
      return {
        slope: 0,
        intercept: sumY / n,
        r2: 0
      };
    }

    const slope = (n * sumXY - sumX * sumY) / denominator;
    const intercept = (sumY - slope * sumX) / n;

    // Calculate R-squared
    const meanY = sumY / n;
    let ssTotal = 0;
    let ssResidual = 0;

    for (const point of points) {
      const predicted = slope * point.x + intercept;
      ssTotal += Math.pow(point.y - meanY, 2);
      ssResidual += Math.pow(point.y - predicted, 2);
    }

    const r2 = ssTotal > 0 ? 1 - (ssResidual / ssTotal) : 0;

    return { slope, intercept, r2 };
  }

  /**
   * Calculate confidence intervals for predictions
   * @param {Array} points - Original data points
   * @param {number} xValue - X value to predict for
   * @param {Object} regression - Regression coefficients
   * @param {number} confidenceLevel - Confidence level (default 0.95)
   * @returns {Object} - { lower, upper }
   */
  calculateConfidenceInterval(points, xValue, regression, confidenceLevel = 0.95) {
    const n = points.length;
    const prediction = regression.slope * xValue + regression.intercept;
    
    // Calculate standard error
    let sumSquaredResiduals = 0;
    let sumX = 0;
    let sumX2 = 0;

    for (const point of points) {
      const predicted = regression.slope * point.x + regression.intercept;
      sumSquaredResiduals += Math.pow(point.y - predicted, 2);
      sumX += point.x;
      sumX2 += point.x * point.x;
    }

    const meanX = sumX / n;
    const se = Math.sqrt(sumSquaredResiduals / (n - 2));
    const ssx = sumX2 - (sumX * sumX) / n;
    
    // t-value for 95% confidence (approximation)
    const tValue = confidenceLevel === 0.95 ? 1.96 : 2.58;
    
    // Standard error of prediction
    const sepred = se * Math.sqrt(1 + 1/n + Math.pow(xValue - meanX, 2) / ssx);
    
    return {
      lower: prediction - tValue * sepred,
      upper: prediction + tValue * sepred
    };
  }

/**
 * Detect resolution from configuration or query
 */
detectResolution() {
  // Check explicit resolution config
  if (this.config.resolution) {
    return;
  }
  
  // Try to detect from predictUntil or other config hints
  const configString = JSON.stringify(this.config).toLowerCase();
  
  if (configString.includes('weekly') || configString.includes('week')) {
    this.config.resolution = 'weekly';
  } else if (configString.includes('monthly') || configString.includes('month')) {
    this.config.resolution = 'monthly';
  } else if (configString.includes('yearly') || configString.includes('year') || configString.includes('annual')) {
    this.config.resolution = 'yearly';
  }
  
  console.log(`Linear Regression: Detected resolution: ${this.config.resolution || 'daily'}`);
}

/**
   * Main prediction method
   * @param {Array} data - The data to analyze
   * @param {string} xAxis - The x-axis column name
   * @param {string} yAxis - The y-axis column name
   * @returns {Object} - { predictions: Array, metadata: Object }
   */
predict(data, xAxis, yAxis) {
  // Validate data
  const validation = this.validateData(data, xAxis, yAxis);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // ADD SAFETY CHECK FOR PREDICTION LIMIT
  const maxAllowedPredictions = 365;
  if (this.config.numPredictions && this.config.numPredictions > maxAllowedPredictions) {
    console.warn(`Limiting predictions from ${this.config.numPredictions} to ${maxAllowedPredictions}`);
    this.config.numPredictions = maxAllowedPredictions;
  }

  // Parse resolution from config or user query
  this.detectResolution();

  // Extract time series with proper aggregation
  const timeSeries = this.extractTimeSeries(
    data, 
    xAxis, 
    yAxis, 
    this.config.aggregationMethod || 'sum'  // Changed from 'average' to 'sum' for consistency
  );

  if (timeSeries.length < 2) {
    throw new Error('Insufficient data points for linear regression');
  }

  // Check if x-axis is dates
  const firstX = timeSeries[0].x;
  const isDateAxis = this.isDateValue(firstX);
  
  let numericPoints;
  let baseDate = null;
  let dateFormat = null;
  let originalDateFormat = null; // Store the actual format from data
  let frequency = null;
  let averageInterval = null;

  if (isDateAxis) {
    // Parse dates and convert to numeric
    const dates = [];
    const validPoints = [];
    
    // Detect the original date format from the first valid date string
    if (timeSeries.length > 0) {
      originalDateFormat = timeSeries[0].x; // Store an example for format detection
    }
    
    for (const point of timeSeries) {
      const parsedDate = parseDate(point.x);
      if (parsedDate) {
        dates.push(parsedDate);
        validPoints.push(point);
      }
    }

    if (validPoints.length < 2) {
      throw new Error('Insufficient valid date points for regression');
    }

    // Detect date format and frequency
    dateFormat = detectDateFormat(timeSeries[0].x);

    // If data was aggregated, use that resolution
    if (timeSeries[0].aggregationType) {
      frequency = timeSeries[0].aggregationType;
      switch (frequency) {
        case 'weekly':
          averageInterval = 7;
          break;
        case 'monthly':
          averageInterval = 30;
          break;
        case 'yearly':
          averageInterval = 365;
          break;
      }
      console.log(`Linear Regression: Using aggregated ${frequency} data with interval ${averageInterval} days`);
    } else {
      // Detect frequency from the data
      const frequencyAnalysis = analyzeDateFrequency(dates);
      frequency = frequencyAnalysis.frequency;
      averageInterval = frequencyAnalysis.averageInterval;
    }

    // Use first date as base
    baseDate = dates[0];
    
    // Convert to numeric using days from base date
    numericPoints = validPoints.map((point, index) => ({
      x: dateToNumeric(dates[index], baseDate),
      y: point.y,
      originalDate: dates[index]
    }));
  } else {
    // Use numeric x values directly
    numericPoints = timeSeries.map((point, index) => ({
      x: parseFloat(point.x) || index,
      y: point.y
    }));
  }

  // Calculate regression
  const regression = this.calculateRegression(numericPoints);

  // DEBUG: Log regression details
  console.log('Regression calculation:', {
    slope: regression.slope,
    intercept: regression.intercept,
    r2: regression.r2,
    dataPoints: numericPoints.length,
    resolution: this.config.resolution || 'daily',
    frequency: frequency,
    firstPoint: numericPoints[0],
    lastPoint: numericPoints[numericPoints.length - 1]
  });
  
  // Generate future points
  let futureXValues;
  
  if (isDateAxis) {
    // Generate future dates based on frequency
    const lastDate = numericPoints[numericPoints.length - 1].originalDate;
    const predictUntil = this.config.predictUntil || null;
    
    const futureDates = generateFutureDatesWithFrequency(
      lastDate,
      predictUntil,
      frequency,
      averageInterval
    );

    // Use the shared method from BasePredictor to calculate predictions needed
    const numPredictions = this.calculatePredictionsNeeded(
      lastDate,
      predictUntil,
      frequency,
      averageInterval
    );

    const limitedDates = futureDates.slice(0, numPredictions);

    futureXValues = limitedDates.map(date => ({
      date: date,
      numeric: dateToNumeric(date, baseDate),
      formatted: originalDateFormat ? formatDate(date, originalDateFormat) : format(date, 'yyyy-MM-dd')
    }));
  } else {
    // Generate numeric future values
    const lastX = numericPoints[numericPoints.length - 1].x;
    const avgInterval = numericPoints.length > 1 
      ? (lastX - numericPoints[0].x) / (numericPoints.length - 1)
      : 1;
    
    const numPredictions = Math.min(this.config.numPredictions || 10, maxAllowedPredictions);
    futureXValues = [];
    
    for (let i = 1; i <= numPredictions; i++) {
      futureXValues.push({
        numeric: lastX + (avgInterval * i),
        formatted: (lastX + (avgInterval * i)).toString()
      });
    }
  }

  // Final safety check
  console.log(`Generating ${futureXValues.length} predictions`);
  if (futureXValues.length > maxAllowedPredictions) {
    console.warn(`Too many predictions requested: ${futureXValues.length}. Limiting to ${maxAllowedPredictions}.`);
    futureXValues = futureXValues.slice(0, maxAllowedPredictions);
  }

  // Generate predictions
  const predictions = futureXValues.map((future, index) => {
    const predictedY = regression.slope * future.numeric + regression.intercept;
    
    const prediction = {
      x: future.formatted,
      y: predictedY,
      _futureIndex: index,
      _isPrediction: true  // Add this marker
    };

    // Add confidence intervals if requested
    if (this.config.includeConfidenceInterval) {
      const confidence = this.calculateConfidenceInterval(
        numericPoints,
        future.numeric,
        regression,
        this.config.confidenceLevel || 0.95
      );
      
      prediction.confidenceLower = confidence.lower;
      prediction.confidenceUpper = confidence.upper;
    }

    return prediction;
  });

  // Format output - pass sample row for all columns
  const formattedPredictions = this.formatPredictionOutput(predictions, xAxis, yAxis, data[0]);

  // Return predictions with metadata
  return {
    predictions: formattedPredictions,
    metadata: {
      method: this.method,
      regression: {
        slope: regression.slope,
        intercept: regression.intercept,
        r2: regression.r2
      },
      dataPoints: timeSeries.length,
      isTimeSeries: isDateAxis,
      frequency: frequency,
      averageInterval: averageInterval,
      confidenceLevel: this.config.confidenceLevel || 0.95,
      config: this.config
    }
  };
}

  /**
   * Check if a value represents a date
   * @param {any} value - Value to check
   * @returns {boolean}
   */
  isDateValue(value) {
    if (!value) return false;
    
    const stringValue = String(value);
    
    // Check common date patterns
    const datePatterns = [
      /^\d{4}-\d{2}-\d{2}/,     // YYYY-MM-DD
      /^\d{2}\/\d{2}\/\d{4}/,   // DD/MM/YYYY or MM/DD/YYYY
      /^\d{4}\/\d{2}\/\d{2}/,   // YYYY/MM/DD
      /^\d{4}-\d{2}$/,          // YYYY-MM
      /^[A-Za-z]+ \d{4}$/       // Month YYYY
    ];

    return datePatterns.some(pattern => pattern.test(stringValue));
  }
}

export default LinearRegressionPredictor;